/**
 * 日期域常量（权威定义）。
 *
 * @canonical 5.3-2.2.3a
 * @归属 date 域（筛选器、SortableDrag、图表日期轴展示）
 * @对外入口 `import { DateFormatString, DateGranularityText, ... } from '@dbl/core/date'`
 * @来源 合并自 `types/dashbord.ts` 与既有本文件定义；引用迁完后 dashbord 不再 export 日期符号。
 */

import { DateGranularity } from './types';

// ==================== Moment / 粒度配置（存量） ====================

/** Moment.js 展示格式映射（key 与 `DateGranularity` 枚举 value / 后端粒度字符串对齐）。 */
export const MomentFormatMap = {
  hour: 'YYYY-MM-DD HH:mm:ss',
  day: 'YYYY-MM-DD',
  week: 'YYYY-MM-DD',
  month: 'YYYY-MM',
} as const;

/** Moment `startOf` / `endOf` 使用的 unit。 */
export const MomentUnitMap = {
  [DateGranularity.HOUR]: 'hour',
  [DateGranularity.DAY]: 'day',
  [DateGranularity.WEEK]: 'isoWeek',
  [DateGranularity.MONTH]: 'months',
} as const;

/** 对比时间：各粒度相对选项（选择器、图例、浮窗共用）。 */
export const GranularityRelativeOptionsConfig = {
  [DateGranularity.HOUR]: [
    { textCn: '上期', textEn: 'Previous period', value: '0-hour', amount: '0', granularity: 'hour' },
    { textCn: '1天前', textEn: '1 day ago', value: '1-day', amount: '1', granularity: 'day' },
    { textCn: '2天前', textEn: '2 days ago', value: '2-day', amount: '2', granularity: 'day' },
    { textCn: '1周前', textEn: '1 week ago', value: '1-week', amount: '1', granularity: 'week' },
  ],
  [DateGranularity.DAY]: [
    { textCn: '上期', textEn: 'Previous period', value: '0-day', amount: '0', granularity: 'day' },
    { textCn: '1天前', textEn: '1 day ago', value: '1-day', amount: '1', granularity: 'day' },
    { textCn: '1周前', textEn: '1 week ago', value: '1-week', amount: '1', granularity: 'week' },
    { textCn: '1个月前', textEn: '1 month ago', value: '1-month', amount: '1', granularity: 'month' },
    { textCn: '1年前', textEn: '1 year ago', value: '1-year', amount: '1', granularity: 'year' },
  ],
  [DateGranularity.WEEK]: [
    { textCn: '上期', textEn: 'Previous period', value: '0-week', amount: '0', granularity: 'week' },
    { textCn: '1周前', textEn: '1 week ago', value: '1-week', amount: '1', granularity: 'week' },
    { textCn: '4周前', textEn: '4 weeks ago', value: '4-week', amount: '4', granularity: 'week' },
    { textCn: '1年前', textEn: '1 year ago', value: '1-year', amount: '1', granularity: 'year' },
  ],
  [DateGranularity.MONTH]: [
    { textCn: '上期', textEn: 'Previous period', value: '0-month', amount: '0', granularity: 'month' },
    { textCn: '1个月前', textEn: '1 month ago', value: '1-month', amount: '1', granularity: 'month' },
    { textCn: '3个月前', textEn: '3 months ago', value: '3-month', amount: '3', granularity: 'month' },
    { textCn: '1年前', textEn: '1 year ago', value: '1-year', amount: '1', granularity: 'year' },
  ],
} as const;

// ==================== 展示文案 / 格式映射（自 dashbord 归位） ====================

/**
 * 日期粒度中英文展示文案。
 * key 与 `DateGranularity` 枚举 value 对齐（hour / day / week / month）。
 */
export const DateGranularityText = {
  hour: {
    en: 'Hour',
    cn: '小时',
  },
  day: {
    en: 'Day',
    cn: '日',
  },
  week: {
    en: 'Week',
    cn: '周',
  },
  month: {
    en: 'Month',
    cn: '月',
  },
} as const;

/**
 * 后端 `to_date_format` 短码 → 前端 Moment/展示格式。
 * 合并 dashbord 与既有 constants 的全部 key，避免 lookup 落空。
 */
export const DateFormatString = {
  YYYYMMDD: 'YYYY-MM-DD',
  MMDDYYYY: 'MM-DD-YYYY',
  MMDD: 'MM-DD',
  YYYYMM: 'YYYY-MM',
  YYYY: 'YYYY',
  MM: 'MM',
  DD: 'DD',
  YYYYMMDDHHMMSS: 'YYYY-MM-DD HH:mm:ss',
  YYYYMMDDTHHMM: 'YYYY-MM-DD HH:mm',
  HHMMSS: 'HH:mm:ss',
  HHMM: 'HH:mm:ss',
  'YYYY-MM-DD': 'YYYY-MM-DD',
  'MM-DD-YYYY': 'MM-DD-YYYY',
  'MM-DD': 'MM-DD',
  'YYYY-MM-DD HH:mm:ss': 'YYYY-MM-DD HH:mm:ss',
} as const;
